// Logical
const NOT_EQUAL = '<>';
const GREATER_EQUAL_THAN = '>=';
const LESS_EQUAL_THAN = '<=';
const EQUAL = '=';
const LESS_THAN = '<';
const GREATER_THAN = '>';

// Arithmetic
const ADDITION = '+';
const SUBSTRACTION = '-';
const MULTIPLICATION = '*';
const DIVISION = '/';

/**
 * WARNING: The order of the operators matters.
 * It is important to set the operators with more than one character at first.
 */
const OPERATORS = [
  // Operators with more than one character
  NOT_EQUAL,
  GREATER_EQUAL_THAN,
  LESS_EQUAL_THAN,
  // Operators with only one character
  LESS_THAN,
  GREATER_THAN,
  EQUAL,
  ADDITION,
  SUBSTRACTION,
  MULTIPLICATION,
  DIVISION
];

/**
 * Encode fron UI to formula JSON format.
 *
 * @param operator The operator.
 * @param constant The constant or undefinded if has not constant.
 * @param varOneId The id of the first variable.
 * @param varTwoId The id of the second variable or undefined if has not second variable.
 * @returns {string}
 */
const encode = ({operator, constant, varOneId, varTwoId}) => {
  const formula = (constant === undefined || constant === null)
    /* Without constant */
    ? {
      formula: 'a ' + operator + ' b',
      variables: [
        { key: 'a', id: varOneId },
        { key: 'b', id: varTwoId }
      ]
    }
    /* With constant */
    : {
      formula: 'a ' + operator + ' ' + constant,
      variables: [
        { key: 'a', id: varOneId }
      ]
    };
  return JSON.stringify(formula);
};

/**
 * Encode fron UI to formula JSON format.
 *
 * @param varId The id of the aggregate variable.
 * @param timeGap The time gap for the second variable operand to get the info from
 * @returns {string}
 */
const encodeGradual = (varId, timeGap) => {
  const formula =
    {
      formula: 'a - b',
      variables: [
        { key: 'a', id: varId },
        { key: 'b', id: varId, timeGap: timeGap }
      ]
    };
  return JSON.stringify(formula);
};

/**
 * Encode fron UI to formula JSON format.
 *
 * @param variable The object of the metrical variable to encode its formula.
 * @returns {string}
 */
const encodeMetrical = (variable) => {
  const metricalFormula = {
    formula: '',
    variables: []
  };
  const variableKeysArray = [];
  variable.variableIdList.forEach((id, index) => {
    const keyChar = numberToKey(index + 1);
    variableKeysArray.push(keyChar);
    metricalFormula.formula +=
      metricalFormula.variables.push({
        key: keyChar,
        id: id
      });
  });
  metricalFormula.formula = variable.metrical + '(' + variableKeysArray.join(', ') + ')';
  return JSON.stringify(metricalFormula);
};

const numberToKey = (n) => {
  let str = '';
  const multiples = Math.ceil(n / 26);
  const charAtCode = n - ((multiples - 1) * 26);
  for (let i = 0; i < multiples; i++) {
    str += String.fromCharCode(charAtCode + 96);
  }
  return '_' + str;
};

/**
 * Decode from string formula to UI object.
 *
 * @param formulaString The formula in string/json format.
 * @returns {{constant: *, operator: *, varOneId: *, varTwoId: *}}
 */
const decode = (formulaString) => {
  const formulaJson = JSON.parse(formulaString);
  const formula = formulaJson.formula;
  const variables = formulaJson.variables;

  let constant = null;
  let varOneId = null;
  let varTwoId = null;
  let operator = null;
  let gradualVarTimeGap = null;

  for (let i = 0; i < OPERATORS.length; i++) {
    const operatorSymbol = OPERATORS[i];
    const tokens = formula.split(operatorSymbol);

    if (tokens && tokens.length > 1) {
      operator = operatorSymbol;

      const a = tokens[0].trim();
      const b = tokens[1].trim();

      // First parameter
      if (isNaN(a)) {
        for (let j = 0; j < variables.length; j++) {
          if (a === variables[j].key) {
            varOneId = variables[j].id;
            break;
          }
        }
      } else {
        constant = a;
      }

      // Second parameter
      if (isNaN(b)) {
        for (let j = 0; j < variables.length; j++) {
          if (b === variables[j].key) {
            varTwoId = variables[j].id;
            if (variables[j].timeGap) {
              gradualVarTimeGap = variables[j].timeGap;
            }
            break;
          }
        }
      } else {
        constant = b;
      }

      break;
    }
  }

  return {
    constant: constant,
    operator: operator,
    varOneId: varOneId,
    varTwoId: varTwoId,
    timeGap: gradualVarTimeGap
  };
};

/**
 * Decode from string formula to UI object.
 *
 * @param formulaString The formula in string/json format.
 * @returns {{metrical: *, varList: *}}
 */
const decodeMetrical = (formulaString) => {
  const formulaJson = JSON.parse(formulaString);
  const metrical = formulaJson.formula.replace(/ *\([^)]*\) */g, '');
  const variables = formulaJson.variables.map(variable => variable.id);
  return {
    metrical: metrical,
    variableList: variables
  };
};

export default {
  decode,
  encode,
  encodeGradual,
  encodeMetrical,
  decodeMetrical
};



// WEBPACK FOOTER //
// ./src/api/utils/formula.js